import { Languages, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLang } from "@/lib/i18n";

const OPTIONS = [
  { code: "fr", label: "Français", short: "FR", flag: "🇫🇷" },
  { code: "ar", label: "العربية", short: "AR", dir: "rtl", flag: "🇸🇦" },
  { code: "tn", label: "تونسي", short: "TN", dir: "rtl", flag: "🇹🇳" },
];

const HAS_SEEN_KEY = "mazra3ti:lang_hint_seen";

export default function LangSwitch({ variant = "dark", className = "" }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Attention-grabbing pulse on first visit
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.localStorage.getItem(HAS_SEEN_KEY)) {
      setHint(true);
      const t = setTimeout(() => {
        setHint(false);
        window.localStorage.setItem(HAS_SEEN_KEY, "1");
      }, 6000);
      return () => clearTimeout(t);
    }
  }, []);

  const current = OPTIONS.find((o) => o.code === lang) || OPTIONS[0];
  const baseBtn =
    variant === "dark"
      ? "text-white border-white/25 hover:bg-white/15 bg-white/5"
      : "text-[#1C3A27] border-[#1C3A27]/30 hover:bg-[#F0F0EB] bg-white";

  const pulseRing = hint
    ? "ring-2 ring-[#E05A3D] ring-offset-2 ring-offset-transparent animate-pulse"
    : "";

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setHint(false); if (typeof window !== "undefined") window.localStorage.setItem(HAS_SEEN_KEY, "1"); }}
        data-testid="lang-switch"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Changer de langue / Choisir la langue"
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-xs font-bold uppercase tracking-wider transition-all ${baseBtn} ${pulseRing}`}
      >
        <Languages size={14} />
        <span className="hidden sm:inline">{current.flag}</span>
        <span dir={current.dir || "ltr"}>{current.short}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid="lang-switch-menu"
          className="absolute end-0 mt-2 w-52 rounded-xl border border-[#E2E4DF] bg-white shadow-2xl overflow-hidden z-50"
        >
          <li className="px-4 py-2 bg-[#F0F0EB] text-[10px] uppercase tracking-widest font-bold text-[#5C6B5C] border-b border-[#E2E4DF]">
            Langue / اللغة
          </li>
          {OPTIONS.map((o) => (
            <li key={o.code}>
              <button
                type="button"
                data-testid={`lang-option-${o.code}`}
                onClick={() => {
                  setLang(o.code);
                  setOpen(false);
                }}
                dir={o.dir || "ltr"}
                className={`w-full flex items-center gap-3 text-start px-4 py-2.5 text-sm font-medium hover:bg-[#F0F0EB] transition-colors ${
                  o.code === lang ? "bg-[#FFF5F1] text-[#E05A3D] font-bold" : "text-[#1C3A27]"
                }`}
              >
                <span className="text-base">{o.flag}</span>
                <span className="flex-1">{o.label}</span>
                {o.code === lang && <span className="text-[#E05A3D]">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
